import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, InputComponent, ModalComponent,
} from '@echospaces-web-monorepo/es-shared-ui';

import type { Space, UpdateSpaceDto } from './space.service';

/**
 * Edit a Space's display metadata — name, description, logo, and sort
 * order. Authoring the Space's structural type (building / floor / room)
 * stays in the Unreal Editor; this is metadata-only.
 *
 * Only org Super-Admins see the trigger (parent's `[canEdit]`), and the
 * backend's CASL gate (`update Space`) double-checks server-side.
 */
@Component({
  selector: 'es-edit-space-modal',
  imports: [FormsModule, ModalComponent, ButtonComponent, FieldComponent, InputComponent],
  template: `
    <es-modal [open]="open" title="Edit space" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field label="Name" [required]="true" [error]="nameError()">
          <es-input placeholder="Welcome lobby" [(ngModel)]="name"></es-input>
        </es-field>

        <es-field label="Description">
          <es-input placeholder="Optional summary shown in listings." [(ngModel)]="description"></es-input>
        </es-field>

        <es-field label="Logo URL" hint="Optional image URL shown next to the space name.">
          <es-input placeholder="https://…" [(ngModel)]="logo"></es-input>
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Saving…' : 'Save' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class EditSpaceModalComponent {
  @Input() open = false;
  @Input() set space(value: Space | null | undefined) {
    if (!value) return;
    this.name = value.name;
    this.description = value.description ?? '';
    this.logo = value.logo ?? '';
  }

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<UpdateSpaceDto>();

  name = '';
  description = '';
  logo = '';

  readonly submitting = signal(false);
  readonly nameError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  onSubmit(): void {
    const name = this.name.trim();
    if (!name) {
      this.nameError.set('Required');
      return;
    }
    this.nameError.set(null);

    const dto: UpdateSpaceDto = { name };
    // Send only fields the user actually typed into — `null` means
    // "clear", undefined means "leave as is". Empty string is treated
    // as a clear for nullable string fields.
    const description = this.description.trim();
    dto.description = description.length > 0 ? description : null;
    const logo = this.logo.trim();
    dto.logo = logo.length > 0 ? logo : null;

    this.submitting.set(true);
    this.submitted.emit(dto);
  }

  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) this.closed.emit();
  }

  onCancel(): void {
    this.nameError.set(null);
    this.submitError.set(null);
    this.closed.emit();
  }
}
